import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtDay } from "@/lib/meta";
import { useStages } from "@/lib/stages";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PromoteDialog({ lead, open, onOpenChange, onPromoted }) {
  const { labels: STAGE_LABEL } = useStages();
  const [matches, setMatches] = useState([]);
  const [mode, setMode] = useState("new"); // "new" | "existing"
  const [selected, setSelected] = useState("");
  const [np, setNp] = useState({ name: "", city: "", num_rooms: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !lead) return;
    setNp({ name: lead.name || "", city: lead.city || "", num_rooms: lead.num_rooms || "", phone: lead.phone || "", address: lead.location || "" });
    setMode("new"); setSelected("");
    api.get("/properties/match", { params: { phone: lead.phone, name: lead.name } })
      .then(({ data }) => {
        setMatches(data.items || []);
        if ((data.items || []).length > 0) { setMode("existing"); setSelected(data.items[0].id); }
      }).catch(() => setMatches([]));
  }, [open, lead]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = mode === "existing" ? { property_id: selected } : { new_property: np };
      const { data } = await api.post(`/leads/${lead.id}/promote`, body);
      toast.success("Promoted to a deal");
      onOpenChange(false);
      onPromoted?.(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="promote-dialog">
        <DialogHeader><DialogTitle>Promote lead to a deal</DialogTitle></DialogHeader>

        {matches.length > 0 && (
          <div className="border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="match-warning">
            <div className="text-xs font-semibold text-amber-800 mb-2">
              Matching propert{matches.length > 1 ? "ies" : "y"} found — review existing deal history before creating a new one.
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {matches.map((m) => (
                <label key={m.id} className={`block border rounded p-2 cursor-pointer text-sm ${mode === "existing" && selected === m.id ? "border-[#0047FF] bg-white" : "border-gray-200 bg-white"}`}>
                  <input type="radio" className="mr-2" checked={mode === "existing" && selected === m.id}
                    onChange={() => { setMode("existing"); setSelected(m.id); }} data-testid={`match-property-${m.id}`} />
                  <span className="font-medium">{m.name}</span> · {m.city}
                  <div className="text-xs text-gray-500 mt-1 ml-5">
                    {m.deals.length === 0 ? "No prior deals" : m.deals.map((d) => `${STAGE_LABEL[d.stage]} (${d.status}, ${fmtDay(d.created_at)})`).join(" · ")}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <label className={`block border rounded p-2 cursor-pointer text-sm ${mode === "new" ? "border-[#0047FF]" : "border-gray-200"}`}>
          <input type="radio" className="mr-2" checked={mode === "new"} onChange={() => setMode("new")} data-testid="promote-new-property" />
          <Plus className="h-3.5 w-3.5 inline mr-1" /> Create a new property
        </label>

        {mode === "new" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Property name</Label><Input value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} className="mt-1" data-testid="np-name" /></div>
            <div><Label>City</Label><Input value={np.city} onChange={(e) => setNp({ ...np, city: e.target.value })} className="mt-1" data-testid="np-city" /></div>
            <div><Label>Rooms</Label><Input value={np.num_rooms} onChange={(e) => setNp({ ...np, num_rooms: e.target.value })} className="mt-1" data-testid="np-rooms" /></div>
            <div><Label>Phone</Label><Input value={np.phone} onChange={(e) => setNp({ ...np, phone: e.target.value })} className="mt-1" data-testid="np-phone" /></div>
            <div><Label>Address</Label><Input value={np.address} onChange={(e) => setNp({ ...np, address: e.target.value })} className="mt-1" data-testid="np-address" /></div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || (mode === "existing" && !selected) || (mode === "new" && !np.name)} data-testid="confirm-promote-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />} Create deal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
